import React, { useState } from 'react';
import './App.css';
import { getHealth, getMe, listAuditEvents, login, validateMfa } from './api/client';
import type { AuditEventItem } from './api/auditTypes';
import { trackEvent } from './telemetry';

function App() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [me, setMe] = useState<string>('');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [health, setHealth] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [auditEvents, setAuditEvents] = useState<AuditEventItem[]>([]);
  const [auditLoading, setAuditLoading] = useState<boolean>(false);
  const [auditAction, setAuditAction] = useState<string>('');
  const [auditUserId, setAuditUserId] = useState<string>('');
  const [mfaRequired, setMfaRequired] = useState<boolean>(false);
  const [mfaUserId, setMfaUserId] = useState<string>('');
  const [mfaCode, setMfaCode] = useState<string>('');
  const [mfaLoading, setMfaLoading] = useState<boolean>(false);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    setMfaRequired(false);
    setMfaUserId('');
    setMfaCode('');
    try {
      const response = await login(email, password);

      if ('mfaRequired' in response && response.mfaRequired) {
        setMfaRequired(true);
        setMfaUserId(response.userId);
        setToken(null);
      } else if ('token' in response && response.token) {
        setToken(response.token);
      } else {
        setError('Unexpected login response');
        trackEvent({
          level: 'warn',
          eventType: 'login.unexpected_response',
          message: 'Unexpected login response shape from API',
          details: { email },
        });
      }
    } catch (e) {
      setToken(null);
      setError(e instanceof Error ? e.message : 'Login failed');
      trackEvent({
        level: 'error',
        eventType: 'login.failed',
        message: 'Login request failed',
        details: { email, error: e instanceof Error ? e.message : String(e) },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!mfaUserId) {
      setError('Missing MFA user context. Please try logging in again.');
      trackEvent({
        level: 'error',
        eventType: 'mfa.missing_context',
        message: 'Missing MFA user context',
        details: { error: 'Missing MFA user context' },
      });
      return;
    }
    if (!mfaCode || mfaCode.length !== 6) {
      setError('Please enter a valid 6-digit MFA code.');
      trackEvent({
        level: 'error',
        eventType: 'mfa.invalid_code',
        message: 'Invalid MFA code',
        details: { error: 'Invalid MFA code' },
      });
      return;
    }

    setError('');
    setMfaLoading(true);
    try {
      const newToken = await validateMfa(mfaUserId, mfaCode);
      setToken(newToken);
      setMfaRequired(false);
    } catch (e) {
      setToken(null);
      setError(e instanceof Error ? e.message : 'MFA validation failed');
      trackEvent({
        level: 'error',
        eventType: 'mfa.failed',
        message: 'MFA validation failed',
        details: { userId: mfaUserId, error: e instanceof Error ? e.message : String(e) },
      });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleLoadAuditEvents = async () => {
    if (!token) {
      setError('You must login first.');
      trackEvent({
        level: 'error',
        eventType: 'audit.load_failed',
        message: 'Audit load failed due to missing token',
        details: { error: 'Missing token' },
      });
      return;
    }
    setError('');
    setAuditLoading(true);
    try {
      const data = await listAuditEvents(token, {
        page: 1,
        limit: 20,
        action: auditAction || undefined,
        userId: auditUserId || undefined,
      });
      setAuditEvents(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load audit events');
      trackEvent({
        level: 'error',
        eventType: 'audit.load_failed',
        message: 'Failed to load audit events',
        details: {
          error: e instanceof Error ? e.message : String(e),
          action: auditAction || undefined,
          userId: auditUserId || undefined,
        },
      });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleLoadMe = async () => {
    if (!token) {
      setError('You must login first.');
      trackEvent({
        level: 'error',
        eventType: 'user.load_failed',
        message: 'User load failed due to missing token',
        details: { error: 'Missing token' },
      });
      return;
    }
    setError('');
    try {
      const data = await getMe(token);
      setMe(JSON.stringify(data.user, null, 2));
      setUserRole(data.user.role);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load user');
      trackEvent({
        level: 'error',
        eventType: 'user.load_failed',
        message: 'Failed to load user profile',
        details: { error: e instanceof Error ? e.message : String(e) },
      });
    }
  };

  const handleHealth = async () => {
    setError('');
    try {
      const data = await getHealth();
      setHealth(JSON.stringify(data, null, 2));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Health check failed');
      trackEvent({
        level: 'error',
        eventType: 'health.failed',
        message: 'Health check request failed',
        details: { error: e instanceof Error ? e.message : String(e) },
      });
    }
  };

  return (
    <div className="App">
      <main className="App-main" aria-labelledby="app-title">
        <h1 id="app-title">E-Unify DevSecOps App</h1>
        <section aria-labelledby="login-heading">
          <h2 id="login-heading">Login</h2>
          <form onSubmit={handleLogin} aria-describedby="login-help">
            <div>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <p id="login-help">Use your development credentials. This form is for testing only.</p>
            <button type="submit" disabled={loading}>
              {loading ? 'Logging in…' : 'Login'}
            </button>
          </form>
          {mfaRequired && (
            <form onSubmit={handleMfaSubmit} aria-labelledby="mfa-heading" style={{ marginTop: '1rem' }}>
              <h3 id="mfa-heading">Multi-Factor Authentication</h3>
              <p>Enter the 6-digit MFA code (use 123456 in development).</p>
              <div>
                <label htmlFor="mfa-code">MFA Code</label>
                <input
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  required
                />
              </div>
              <button type="submit" disabled={mfaLoading}>
                {mfaLoading ? 'Verifying…' : 'Verify MFA'}
              </button>
            </form>
          )}
          {token && (
            <p aria-live="polite">Token acquired. You can now load profile and health.</p>
          )}
        </section>

        <section aria-labelledby="dashboard-heading">
          <h2 id="dashboard-heading">Health Dashboard</h2>
          <div>
            <button type="button" onClick={handleLoadMe} disabled={!token}>
              Load User Profile
            </button>
            <button type="button" onClick={handleHealth}>
              Check API Health
            </button>
          </div>
          {error && (
            <div role="alert" className="App-alert">
              {error}
            </div>
          )}
          <div style={{ marginTop: '1rem' }}>
            <h3>User</h3>
            <pre aria-live="polite">{me || 'No user loaded yet.'}</pre>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <h3>API Health</h3>
            <pre aria-live="polite">{health || 'No health check run yet.'}</pre>
          </div>
        </section>

        {token && userRole && ['admin', 'security-officer', 'auditor'].includes(userRole) && (
          <section aria-labelledby="audit-heading">
            <h2 id="audit-heading">Audit Explorer</h2>
            <p>Recent audit events (first 20), for compliance and review.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem' }}>
              <div>
                <label htmlFor="audit-action">Action</label>
                <input
                  id="audit-action"
                  name="audit-action"
                  type="text"
                  value={auditAction}
                  onChange={(e) => setAuditAction(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="audit-user">User ID or Email</label>
                <input
                  id="audit-user"
                  name="audit-user"
                  type="text"
                  value={auditUserId}
                  onChange={(e) => setAuditUserId(e.target.value)}
                />
              </div>
            </div>
            <button type="button" onClick={handleLoadAuditEvents} disabled={auditLoading}>
              {auditLoading ? 'Loading events…' : 'Load Audit Events'}
            </button>
            {auditEvents.length > 0 && (
              <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
                <table aria-label="Audit events table">
                  <caption>Latest audit events</caption>
                  <thead>
                    <tr>
                      <th scope="col">Timestamp</th>
                      <th scope="col">Action</th>
                      <th scope="col">Actor Email</th>
                      <th scope="col">Entity</th>
                      <th scope="col">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEvents.map((evt) => (
                      <tr key={evt._id}>
                        <td>{new Date(evt.timestamp).toLocaleString()}</td>
                        <td>{evt.action}</td>
                        <td>{evt.actor?.email || '—'}</td>
                        <td>
                          {evt.entity?.type || '—'}
                          {evt.entity?.id ? ` / ${evt.entity.id}` : ''}
                        </td>
                        <td>{evt.severity || 'low'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

export default App;
